import { Component, OnDestroy, inject, output, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, Subject } from 'rxjs';
import { ApiErrorResponse } from '@bn/api-interfaces';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { ClickActionEvent, HttpErrorInterceptor } from '@bn/shared/entities/index';
import { PullZoneStore, StoreRequest } from '@bn/shared/state-store';
import { RegexUtils } from '@bn/shared/utils';
import { MarkAllTouchedDirective } from '@bn/web-ui';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { ModalState } from '@bn/web-ui/dialog';
import { InputComponent } from '@bn/web-ui/input/input.component';
import { ShowApiControlError } from '@bn/web-ui/standalone/formcontrol-errors';

@Component({
  selector: 'bn-dashboard-select-custom-hostname-modal',
  imports: [ButtonComponent, FaIconComponent, FormsModule, InputComponent, MarkAllTouchedDirective, ReactiveFormsModule],
  templateUrl: './select-custom-hostname-modal.component.html',
})
export class SelectCustomHostnameModalComponent extends AbstractLazyDirective implements OnDestroy {
  bsModalRef = inject(BsModalRef);

  public readonly addHostnameClicked = output<string>();

  public readonly hostnameInput = viewChild.required<InputComponent>('hostnameInput');

  readonly #pullZoneStore = inject(PullZoneStore);

  public isFinishedAddHostname = this.#pullZoneStore.isFinished(StoreRequest.Update, ['AddHostname']);
  public isLoadingAddHostname = this.#pullZoneStore.isLoading(StoreRequest.Update, ['AddHostname']);
  public addHostnameError = this.#pullZoneStore.getError(StoreRequest.Update, ['AddHostname']);

  public customHostnameForm = new FormGroup({
    Hostname: new FormControl('', [Validators.required, Validators.pattern(RegexUtils.SimpleHostnameWildcard)]),
  });

  public data?: ModalState;

  private readonly onAction = new Subject<ClickActionEvent>();

  constructor() {
    super();

    this.subscribeToStoreSignals();
  }

  public ngOnDestroy(): void {
    this.#pullZoneStore.clearUpdated();
  }

  public onSubmit(): void {
    if (this.customHostnameForm.invalid) {
      return;
    }

    const hostname = this.customHostnameForm.get('Hostname')?.value ?? '';

    this.onAction.next({
      action: 'confirm',
      data: {
        entity: {
          hostname,
        },
      },
    });

    this.addHostnameClicked.emit(hostname);
  }

  public onCloseDialog(): void {
    this.onAction.next({
      action: 'cancel',
    });
  }

  public getOnAction$(): Observable<ClickActionEvent> {
    return this.onAction.asObservable();
  }

  public subscribeToStoreSignals(): void {
    toObservable(this.addHostnameError)
      .pipe(takeUntilDestroyed())
      .subscribe((addHostnameError) => {
        if (!addHostnameError) return;

        const errorObj: ApiErrorResponse = {
          ...addHostnameError,
        };

        if (errorObj.error.ErrorKey?.includes('hostname_invalid')) errorObj.error.Field = 'Hostname';
        // must throw error for every other type of error
        if (!ShowApiControlError(this.customHostnameForm, errorObj)) HttpErrorInterceptor.showToastError(errorObj, this.toastr);
      });
  }
}
